<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeModal from "@dsk/components/LeModal.vue";
import type { ModalProps } from "@dsk/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import type { Dictionary } from "@/types";

import LeDictionaryCover from "./LeDictionaryCover.vue";

interface Props {
  dictionary: Dictionary;
}

const props = defineProps<ModalProps & Props>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();

const aboutTextMap: Record<string, string> = {
  "cent-accounting-dada": t("dictionaries.cent-accounting-dada.long-description"),
  "cent-accounting-daen": t("dictionaries.cent-accounting-daen.long-description"),
  "cent-accounting-enda": t("dictionaries.cent-accounting-enda.long-description"),
  "cent-accounting-enen": t("dictionaries.cent-accounting-enen.long-description"),
  "cent-concepts-dada": t("dictionaries.cent-concepts-dada.long-description"),
  "cent-dada": t("dictionaries.cent-dada.long-description"),
  "cent-definitions-dada": t("dictionaries.cent-definitions-dada.long-description"),
  "cent-grammar-dada": t("dictionaries.cent-grammar-dada.long-description"),
  "cent-idioms-dada": t("dictionaries.cent-idioms-dada.long-description"),
  "cent-legal-enda": t("dictionaries.cent-legal-enda.long-description"),
  "cent-music-dada": t("dictionaries.cent-music-dada.long-description"),
  "cent-thesaurus-dada": t("dictionaries.cent-thesaurus-dada.long-description"),
  "cent-usage-dada": t("dictionaries.cent-usage-dada.long-description"),
  "chemical-dada": t("dictionaries.chemical-dada.long-description"),
  "clau-deda": t("dictionaries.clau-deda.long-description"),
  "clau-enda": t("dictionaries.clau-enda.long-description"),
  "corn-deen": t("dictionaries.corn-deen.long-description"),
  "corn-dees": t("dictionaries.corn-dees.long-description"),
  "corn-defr": t("dictionaries.corn-defr.long-description"),
  "corn-deit": t("dictionaries.corn-deit.long-description"),
  "dsn-spelling-dada": t("dictionaries.dsn-spelling-dada.long-description"),
  "duden-dede": t("dictionaries.duden-dede.long-description"),
  "gyldendal-adv-dade": t("dictionaries.gyldendal-adv-dade.long-description"),
  "gyldendal-adv-daen": t("dictionaries.gyldendal-adv-daen.long-description"),
  "gyldendal-adv-frda": t("dictionaries.gyldendal-adv-frda.long-description"),
  "gyldendal-agri-daen": t("dictionaries.gyldendal-agri-daen.long-description"),
  "gyldendal-bai-trade-daen": t("dictionaries.gyldendal-bai-trade-daen.long-description"),
  "gyldendal-bh-dafr": t("dictionaries.gyldendal-bh-dafr.long-description"),
  "gyldendal-biz-dade": t("dictionaries.gyldendal-biz-dade.long-description"),
  "gyldendal-biz-daen": t("dictionaries.gyldendal-biz-daen.long-description"),
  "gyldendal-chem-daen": t("dictionaries.gyldendal-chem-daen.long-description"),
  "gyldendal-clinical-dada": t("dictionaries.gyldendal-clinical-dada.long-description"),
  "gyldendal-constr-dade": t("dictionaries.gyldendal-constr-dade.long-description"),
  "gyldendal-constr-daen": t("dictionaries.gyldendal-constr-daen.long-description"),
  "gyldendal-definition-dada": t("dictionaries.gyldendal-definition-dada.long-description"),
  "gyldendal-design-daen": t("dictionaries.gyldendal-design-daen.long-description"),
  "gyldendal-econ-daen": t("dictionaries.gyldendal-econ-daen.long-description"),
  "gyldendal-environ-daen": t("dictionaries.gyldendal-environ-daen.long-description"),
  "gyldendal-foreign-dada": t("dictionaries.gyldendal-foreign-dada.long-description"),
  "gyldendal-informatics-dade": t("dictionaries.gyldendal-informatics-dade.long-description"),
  "gyldendal-informatics-daen": t("dictionaries.gyldendal-informatics-daen.long-description"),
  "gyldendal-legal-dade": t("dictionaries.gyldendal-legal-dade.long-description"),
  "gyldendal-legal-daen": t("dictionaries.gyldendal-legal-daen.long-description"),
  "gyldendal-legal-daes": t("dictionaries.gyldendal-legal-daes.long-description"),
  "gyldendal-lexicon-dada": t("dictionaries.gyldendal-lexicon-dada.long-description"),
  "gyldendal-mechanical-daen": t("dictionaries.gyldendal-mechanical-daen.long-description"),
  "gyldendal-medical-daen": t("dictionaries.gyldendal-medical-daen.long-description"),
  "gyldendal-ox-biz-enen": t("dictionaries.gyldendal-ox-biz-enen.long-description"),
  "gyldendal-ox-econ-enen": t("dictionaries.gyldendal-ox-econ-enen.long-description"),
  "gyldendal-ox-enen": t("dictionaries.gyldendal-ox-enen.long-description"),
  "gyldendal-ox-thesau-enen": t("dictionaries.gyldendal-ox-thesau-enen.long-description"),
  "gyldendal-pedagogic-dada": t("dictionaries.gyldendal-pedagogic-dada.long-description"),
  "gyldendal-pro-daen": t("dictionaries.gyldendal-pro-daen.long-description"),
  "gyldendal-psych-dada": t("dictionaries.gyldendal-psych-dada.long-description"),
  "gyldendal-red-dade": t("dictionaries.gyldendal-red-dade.long-description"),
  "gyldendal-red-daen": t("dictionaries.gyldendal-red-daen.long-description"),
  "gyldendal-red-daes": t("dictionaries.gyldendal-red-daes.long-description"),
  "gyldendal-red-dafa": t("dictionaries.gyldendal-red-dafa.long-description"),
  "gyldendal-red-dafr": t("dictionaries.gyldendal-red-dafr.long-description"),
  "gyldendal-red-dait": t("dictionaries.gyldendal-red-dait.long-description"),
  "gyldendal-red-dala": t("dictionaries.gyldendal-red-dala.long-description"),
  "gyldendal-red-dano": t("dictionaries.gyldendal-red-dano.long-description"),
  "gyldendal-red-dapt": t("dictionaries.gyldendal-red-dapt.long-description"),
  "gyldendal-red-daru": t("dictionaries.gyldendal-red-daru.long-description"),
  "gyldendal-red-dasv": t("dictionaries.gyldendal-red-dasv.long-description"),
  "gyldendal-red-datr": t("dictionaries.gyldendal-red-datr.long-description"),
  "gyldendal-socio-dada": t("dictionaries.gyldendal-socio-dada.long-description"),
  "gyldendal-spelling-dada": t("dictionaries.gyldendal-spelling-dada.long-description"),
  "gyldendal-synonym-dada": t("dictionaries.gyldendal-synonym-dada.long-description"),
  "gyldendal-technical-dade": t("dictionaries.gyldendal-technical-dade.long-description"),
  "gyldendal-technical-daen": t("dictionaries.gyldendal-technical-daen.long-description"),
  "kdict-en-ptbr": t("dictionaries.kdict-en-ptbr.long-description"),
  "kdict-enaf": t("dictionaries.kdict-enaf.long-description"),
  "kdict-enar": t("dictionaries.kdict-enar.long-description"),
  "kdict-enbg": t("dictionaries.kdict-enbg.long-description"),
  "kdict-encs": t("dictionaries.kdict-encs.long-description"),
  "kdict-endr": t("dictionaries.kdict-endr.long-description"),
  "kdict-enel": t("dictionaries.kdict-enel.long-description"),
  "kdict-enes": t("dictionaries.kdict-enes.long-description"),
  "kdict-enet": t("dictionaries.kdict-enet.long-description"),
  "kdict-enfa": t("dictionaries.kdict-enfa.long-description"),
  "kdict-enfi": t("dictionaries.kdict-enfi.long-description"),
  "kdict-enfr": t("dictionaries.kdict-enfr.long-description"),
  "kdict-enhe": t("dictionaries.kdict-enhe.long-description"),
  "kdict-enhi": t("dictionaries.kdict-enhi.long-description"),
  "kdict-enhr": t("dictionaries.kdict-enhr.long-description"),
  "kdict-enhu": t("dictionaries.kdict-enhu.long-description"),
  "kdict-enid": t("dictionaries.kdict-enid.long-description"),
  "kdict-enis": t("dictionaries.kdict-enis.long-description"),
  "kdict-enit": t("dictionaries.kdict-enit.long-description"),
  "kdict-enja": t("dictionaries.kdict-enja.long-description"),
  "kdict-enko": t("dictionaries.kdict-enko.long-description"),
  "kdict-enlt": t("dictionaries.kdict-enlt.long-description"),
  "kdict-enlv": t("dictionaries.kdict-enlv.long-description"),
  "kdict-enms": t("dictionaries.kdict-enms.long-description"),
  "kdict-ennl": t("dictionaries.kdict-ennl.long-description"),
  "kdict-enpl": t("dictionaries.kdict-enpl.long-description"),
  "kdict-enps": t("dictionaries.kdict-enps.long-description"),
  "kdict-enpt": t("dictionaries.kdict-enpt.long-description"),
  "kdict-enro": t("dictionaries.kdict-enro.long-description"),
  "kdict-enru": t("dictionaries.kdict-enru.long-description"),
  "kdict-ensk": t("dictionaries.kdict-ensk.long-description"),
  "kdict-ensl": t("dictionaries.kdict-ensl.long-description"),
  "kdict-ensr": t("dictionaries.kdict-ensr.long-description"),
  "kdict-enth": t("dictionaries.kdict-enth.long-description"),
  "kdict-entr": t("dictionaries.kdict-entr.long-description"),
  "kdict-entw": t("dictionaries.kdict-entw.long-description"),
  "kdict-enuk": t("dictionaries.kdict-enuk.long-description"),
  "kdict-enur": t("dictionaries.kdict-enur.long-description"),
  "kdict-envi": t("dictionaries.kdict-envi.long-description"),
  "ne-sven": t("dictionaries.ne-sven.long-description"),
  "ordbogen-dade": t("dictionaries.ordbogen-dade.long-description"),
  "ordbogen-daen": t("dictionaries.ordbogen-daen.long-description"),
  "ordbogen-daes": t("dictionaries.ordbogen-daes.long-description"),
  "ordbogen-dafr": t("dictionaries.ordbogen-dafr.long-description"),
  "ordbogen-dait": t("dictionaries.ordbogen-dait.long-description"),
  "ordbogen-dapt": t("dictionaries.ordbogen-dapt.long-description"),
  "ordbogen-dasv": t("dictionaries.ordbogen-dasv.long-description"),
  "ordbogen-deen": t("dictionaries.ordbogen-deen.long-description"),
  "ordbogen-daar": t("dictionaries.ordbogen-daar.long-description"),
  "ordbogen-klkl": t("dictionaries.ordbogen-klkl.long-description"),
  "ox-deen": t("dictionaries.ox-deen.long-description"),
  "ox-en-zhhans": t("dictionaries.ox-en-zhhans.long-description"),
  "ox-grosse-deen": t("dictionaries.ox-grosse-deen.long-description"),
  "pol-advanced-daen": t("dictionaries.pol-advanced-daen.long-description"),
  "pol-childrens-dada": t("dictionaries.pol-childrens-dada.long-description"),
  "pol-childrens-daen": t("dictionaries.pol-childrens-daen.long-description"),
  "pol-current-dada": t("dictionaries.pol-current-dada.long-description"),
  "pol-dade": t("dictionaries.pol-dade.long-description"),
  "pol-daen": t("dictionaries.pol-daen.long-description"),
  "pol-dafr": t("dictionaries.pol-dafr.long-description"),
  "pol-foreign-dada": t("dictionaries.pol-foreign-dada.long-description"),
  "pol-rhymes-dada": t("dictionaries.pol-rhymes-dada.long-description"),
  "pol-school-dada": t("dictionaries.pol-school-dada.long-description"),
  "pol-simp-current-dada": t("dictionaries.pol-simp-current-dada.long-description"),
  "pol-thesaurus-dada": t("dictionaries.pol-thesaurus-dada.long-description"),
  "random-enen": t("dictionaries.random-enen.long-description"),
  "realestate-frda": t("dictionaries.realestate-frda.long-description"),
  "vega-noen": t("dictionaries.vega-noen.long-description"),
  "veterania-auto-enda": t("dictionaries.veterania-auto-enda.long-description"),
  "vind-daen": t("dictionaries.vind-daen.long-description")
};

const aboutText = computed<string>(() => aboutTextMap[props.dictionary.id] ?? "");
const imagePath = computed<string>(() => `/local-cdn/dictionaries/article-examples/${props.dictionary.id}`);

const onClose = () => {
  emit("close");
};
</script>

<template>
  <LeModal class="le-about-dictionary-modal" size="lg" :is-open="props.isOpen" @close="onClose()">
    <template #title>
      <div class="le-about-dictionary-modal__header">
        <LeDictionaryCover
          class="le-about-dictionary-modal__dictionary-cover"
          :dictionary="props.dictionary"
          size="large"
        />
        <span class="le-about-dictionary-modal__dictionary-title">{{ dictionary.title }}</span>
        <span class="le-about-dictionary-modal__dictionary-publisher">{{ dictionary.publisher }}</span>
      </div>
    </template>

    <template #body>
      <p v-if="dictionary.isbns.length > 0" class="le-about-dictionary-modal__isbn-list">
        {{ t("le-about-dictionary.modal-dictionary.isbn.text") }} {{ dictionary.isbns.join(", ") }}
      </p>

      <div class="le-about-dictionary-modal__about-text" v-html="aboutText"></div>

      <p class="le-about-dictionary-modal__preview-label">{{ t("le-about-dictionary.example.text") }}</p>

      <picture>
        <source :srcset="`${imagePath}.png`" media="(prefers-reduced-motion: reduce)" />
        <img class="le-about-dictionary-modal__preview-image" :src="`${imagePath}.webp`" alt="" />
      </picture>
    </template>

    <template #buttons>
      <LeButton variant="outline" @click="onClose()">
        {{ t("le-about-dictionary.modal-dictionary.back.button") }}
      </LeButton>
      <LeButton tag="router-link" :to="{ name: 'subscription' }">
        {{ t("le-about-dictionary.modal-dictionary.access.button") }}
      </LeButton>
    </template>
  </LeModal>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-about-dictionary-modal {
  &__isbn-list {
    margin-bottom: 12px;
    .body-text("xs", @style: "italic");
  }

  &__about-text {
    margin-bottom: 24px;
    .body-text("s");

    ::v-deep(p) {
      margin-bottom: 15px;
      white-space: pre-wrap;
    }
  }

  &__header {
    display: grid;
    grid-template-areas:
      "cover title"
      "cover publisher";
    gap: 4px 12px;
    align-items: center;
    justify-content: start;
    background: @colors[@neutrals][100];
  }

  &__dictionary-cover {
    grid-area: cover;
  }

  &__dictionary-title {
    grid-area: title;
    height: 20px;
    overflow: hidden;
    color: @colors[@neutrals][400];
    text-overflow: ellipsis;
    white-space: nowrap;
    .body-text("s", "semi-bold");
  }

  &__dictionary-publisher {
    grid-area: publisher;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    .body-text("s");
  }

  &__preview {
    &-label {
      margin-bottom: 4px;
      .body-text("xs", @style: "italic");
    }

    &-image {
      width: 897px;
      border: 1px solid @colors[@secondary][300];
    }
  }

  &__buttons-container {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
}
</style>
